// src/pages/usa/puertorico/Aguadilla.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";
import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Aguadilla ALWAYS goes back to Puerto Rico map
export const backRoute = "/map/puertorico";

export default function SanJuan() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>San Juan, Puerto Rico</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
            <h2>🏞️ Discover San Juan, Puerto Rico</h2>

<p>
  San Juan is located on the northeastern coast of Puerto Rico and is the island’s
  vibrant capital — a mix of colonial history, colorful streets, oceanfront views,
  and lively culture. From the massive walls of El Morro to the blue cobblestone
  streets of Old San Juan, this city blends centuries of heritage with modern
  Caribbean energy.
</p>

<h3>🏰 Castillo San Felipe del Morro (El Morro)</h3>
<p>
  El Morro is one of Puerto Rico’s most iconic landmarks — a 16th‑century fortress
  built by the Spanish to guard the entrance to San Juan Bay. Its tunnels, barracks,
  and ocean‑facing cannons tell the story of the island’s strategic importance.
  The views of the Atlantic from the ramparts are some of the best in the Caribbean.
</p>

<h3>🌈 The Streets of Old San Juan</h3>
<p>
  Old San Juan is famous for its colorful colonial buildings, blue cobblestone
  streets, and lively plazas. Walking through Calle Fortaleza, Calle San Sebastián,
  and the surrounding alleys feels like stepping into a living museum filled with
  cafés, artisan shops, historic churches, and vibrant street art.
</p>

<h3>🚢 San Juan Cruise Port</h3>
<p>
  San Juan is one of the busiest cruise ports in the Caribbean. Ships dock right
  beside Old San Juan, allowing visitors to walk directly into the historic district.
  The waterfront is lined with restaurants, shops, and scenic views of the bay,
  making it a perfect starting point for exploring the city.
</p>

<h3>🌙 Nightlife in San Juan</h3>
<p>
  San Juan comes alive at night with a mix of salsa bars, lounges, rooftop spots,
  and live‑music venues. Areas like La Placita de Santurce and Calle San Sebastián
  offer everything from dancing to relaxed cocktails, creating a fun and energetic
  Caribbean atmosphere.
</p>

<h3>🌊 La Perla – The Oceanfront Community Beside El Morro</h3>
<p>
  La Perla is a historic seaside neighborhood located just outside the old city
  walls, between El Morro and Castillo San Cristóbal. It became internationally
  known after appearing in the <strong>“Despacito” music video by Luis Fonsi and
  Daddy Yankee</strong>, which was filmed in several spots throughout the community.
</p>

<p>
  La Perla is a local residential area, not a tourist attraction. Visitors are
  welcome as long as they are respectful. It’s important to be mindful of residents,
  ask permission before taking photos, and follow local guidance. If you visit,
  do so during the day and always be courteous — you are a guest in their community.
</p>

            <h3>🚗 Getting There</h3>

<p>
  San Juan is easy to reach thanks to <strong>Luis Muñoz Marín International Airport (SJU)</strong>,
  the busiest airport in the Caribbean and the main gateway to Puerto Rico. The airport is located
  in Carolina, just <strong>15–20 minutes from Old San Juan</strong> by car, making it one of the most
  convenient arrival points in the region. SJU offers nonstop flights from major U.S. cities like
  Orlando, Miami, New York, Atlanta, Chicago, Dallas, Boston, and Houston, along with routes to
  Panama, the Dominican Republic, and other Caribbean destinations. Because Puerto Rico is a U.S.
  territory, <strong>U.S. citizens do not need a passport</strong> to fly to San Juan — a valid federal
  or state‑issued photo ID is enough. Transportation options from the airport include Uber, official
  taxis with flat‑rate zones, and onsite car rentals, making it simple to reach Isla Verde, Condado,
  or Old San Juan shortly after landing.
</p>


            <h3>🌟 My Personal Observation</h3>

<ul>
  <li>
    <div className="place-block">
      <strong>The Old San Juan Street:</strong> Walking the historic streets of Old San Juan.
      <br />
      The old city is famous for its iconic <strong>blue cobblestone streets</strong> (adoquines),
      originally brought from Spain in the 1700s. These stones give Old San Juan its unique charm
      and make every walk feel like stepping back in time. As you explore, you’ll find beautiful
      colonial buildings, local shops, cafés, and several important landmarks.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Plaza de Armas:</strong> One of the main plazas of Old San Juan.
      <br />
      Plaza de Armas is a classic gathering spot surrounded by historic architecture, fountains,
      and open‑air seating. It’s a perfect place to relax, enjoy a coffee, and watch the daily
      rhythm of the city.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Museums:</strong> Art, culture, and history in the heart of the old city.
      <br />
      Old San Juan is home to several museums that preserve Puerto Rico’s heritage, including the
      <em>Museo de las Américas</em> and the <em>Casa Blanca Museum</em>. These spaces offer exhibits
      on art, culture, and the island’s colonial past.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>La Fortaleza (The Governor’s House):</strong> A historic executive mansion.
      <br />
      Located on Calle Fortaleza, this is the oldest executive mansion still in use in the Western
      Hemisphere. Its pastel‑blue façade and historic significance make it one of the most
      photographed buildings in Old San Juan.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>El Capitolio:</strong> Puerto Rico’s Capitol building.
      <br />
      At the entrance of Old San Juan you’ll find this impressive neoclassical structure facing the
      Atlantic Ocean. Visitors can admire its architecture from the outside or explore the interior
      on guided tours to learn more about Puerto Rico’s government and history.
    </div>
  </li>
</ul>


            <h3>🥾 Other Hidden Gems</h3>

<ul>
  <li>
    <div className="place-block">
      <strong>Distrito T‑Mobile:</strong>
      <br />
      One of San Juan’s newest entertainment districts, located next to the Puerto Rico Convention Center.
      It features massive LED screens, restaurants, bars, live music, a zipline, and an outdoor plaza that
      lights up at night. It’s a great spot for families during the day and a fun nightlife destination
      after sunset.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Kayak & Water Activities:</strong>
      <br />
      San Juan offers several water adventures, including kayaking in the Condado Lagoon, paddleboarding,
      and guided eco‑tours where you can spot manatees and enjoy calm, crystal‑clear waters. It’s a peaceful
      way to experience the city from a different angle.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Nightlife Events:</strong>
      <br />
      Beyond Old San Juan, areas like Condado and La Placita de Santurce offer vibrant nightlife with
      live music, salsa dancing, rooftop lounges, and open‑air bars. Weekends are especially lively, with
      locals and visitors filling the streets for food, drinks, and music.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Fiestas Patronales de San Juan:</strong>
      <br />
      San Juan celebrates its annual <em>Fiestas Patronales</em> in honor of <strong>San Juan Bautista</strong>.
      These festivities usually take place in <strong>June</strong> and include live music, cultural events,
      artisans, food kiosks, and family‑friendly activities. It’s one of the most traditional celebrations
      on the island and a great way to experience Puerto Rican culture up close.
    </div>
  </li>
</ul>


            <h3>🛏️ Places to Stay in San Juan</h3>

<p>
  San Juan has excellent hotel options across Condado, Isla Verde, Miramar, and Old San Juan.
  All of the stays listed below are available on <strong>Booking.com</strong> and
  <strong>Expedia</strong>. Always double‑check availability and reviews before booking.
</p>

<ul>
  <li>
    <div className="place-block">
      <strong>DoubleTree by Hilton San Juan:</strong>
      Modern hotel in Condado with pool, spa, and beach shuttle.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Hyatt Place San Juan:</strong>
      Popular stay near the Convention Center with a large pool area and café/bar.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>The Tryst Beachfront Hotel:</strong>
      Trendy boutique hotel directly on Condado Beach with a beachfront bar.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Sheraton Puerto Rico Resort & Casino:</strong>
      Upscale resort with casino, infinity pool, and dining options. Steps from Distrito T‑Mobile.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>San Juan Marriott Resort & Stellaris Casino:</strong>
      Premium beachfront resort with pools, bars, restaurants, and a lively atmosphere.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Caribe Hilton:</strong>
      Iconic oceanfront resort with private beach, tropical gardens, multiple pools, and restaurants.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Trópica Beach Hotel:</strong>
      Stylish boutique hotel near Ocean Park Beach with a relaxed, modern vibe.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>
</ul>


            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>

            <h3>🎥 My Travel Videos</h3>

            <p>
              Here is the video of the Old San Juan.
            </p>

            <p>
              <a
                href="https://youtu.be/-pD_4AjSjZI?si=CG0SPaGec6SO1Hfq"
                target="_blank"
                rel="noopener noreferrer"
              >
                Old San Juan, Puerto Rico (YouTube Video)
              </a>
            </p>


          </div>

          {/* RIGHT COLUMN */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/-pD_4AjSjZI?si=CG0SPaGec6SO1Hfq"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/puertorico/sanjuan/morro.png"
                alt="Survival Beach"
              />
            </a>

           
            <img
              src="/images/usa/puertorico/sanjuan/barco.jpg"
              alt="San Juan"
            />
            <img
              src="/images/usa/puertorico/sanjuan/barco2.jpg"
              alt="San Juan"
            />
            <img
              src="/images/usa/puertorico/sanjuan/barco3.jpg"
              alt="San Juan"
            />
            <img
              src="/images/usa/puertorico/sanjuan/castle1.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/castle2.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/castle3.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/castle4.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/castle5.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/castle6.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/street1.jpg"
              alt="San Jua"
            />
            <img
              src="/images/usa/puertorico/sanjuan/street2.jpg"
              alt="San Juan"
            />
            <img
              src="/images/usa/puertorico/sanjuan/street3.jpg"
              alt="San Juan"
            />
            <img
              src="/images/usa/puertorico/sanjuan/street5.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/street7.jpg"
              alt="San Juan"
            />
             <img
              src="/images/usa/puertorico/sanjuan/street9.jpg"
              alt="San Juan"
            />


          </div>
        </section>
      </div>

      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
